import React, { useCallback, useMemo, useState } from 'react';
import BrochureHeroCta from './BrochureHeroCta';
import BookmarksScrubber from './BookmarksScrubber';
import AdvisorCtaFooter from './AdvisorCtaFooter';
import BrochureAlumniCarousel from './BrochureAlumniCarousel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { shuffle } from 'lodash';
import { LEAD_GEN_BROCHURE_VIEW_SIDEBAR } from '@src/constants/LeadGenerationConstants/index';
import useBookmarkNavigation from '@src/hooks/useBookmarkNavigation';
import useGraduatesData from '@src/hooks/useGraduatesData';

// MobileBrochureSidebar — fixed-position bottom sheet for small viewports.
//
// Default (closed) state: small rail showing the program name + "Book your
// demo now" CTA. Tap the chevron/handle to expand. Expanded state reveals
// bookmarks, alumni carousel, and the advisor footer on top of the PDF,
// without leaving the page.
//
// Rendering note: this component is only mounted below the `lg` breakpoint
// by BrochureViewPage, so no media queries are necessary inside it.
const MobileBrochureSidebar = ({
  programName,
  bookmarks,
  currentPage,
  numPages,
  onJumpToPage,
  analytics,
}) => {
  const [open, setOpen] = useState(false);

  const graduatesData = useGraduatesData();

  const shuffledGraduates = useMemo(
    () => shuffle(graduatesData).slice(0, 5),
    [graduatesData],
  );

  const closeSheet = useCallback(() => setOpen(false), []);

  const handleBookmarkClick = useBookmarkNavigation({
    onJumpToPage,
    reportBookmarkClick: analytics?.reportBookmarkClick,
    afterNavigate: closeSheet,
  });

  return (
    <>
      {/* Backdrop — only shown when expanded */}
      {open && (
        <button
          type="button"
          aria-label="Close brochure menu"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[40] bg-black/40 backdrop-blur-sm"
        />
      )}

      <div
        className={`fixed left-0 right-0 bottom-0 z-[50] flex flex-col rounded-t-[20px] bg-v5-green-100 shadow-[0_-4px_24px_rgba(0,0,0,0.15)] transition-[max-height] duration-300 ease-out ${
          open ? 'max-h-[85dvh]' : 'max-h-[200px]'
        }`}
      >
        {/* Collapse / Expand toggle button */}
        <button
          type="button"
          aria-label={open ? 'Collapse brochure menu' : 'Expand brochure menu'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="flex w-full flex-col items-center py-3 text-v5-neutral-400 transition-colors hover:text-v5-green-600"
        >
          <FontAwesomeIcon
            icon={['fas', open ? 'angle-down' : 'angle-up']}
            className="text-xl"
          />
        </button>

        {/* Always-visible header block */}
        <div className="px-5 pt-2 pb-4">
          <BrochureHeroCta
            programName={programName}
            buttonLocation={LEAD_GEN_BROCHURE_VIEW_SIDEBAR}
            onCtaClick={analytics?.reportDemoCtaClick}
          />
        </div>

        {/* Expandable body */}
        <div
          className={`flex-1 overflow-y-auto px-5 pb-6 ${
            open ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          {bookmarks && bookmarks.length > 0 && (
            <div className="mb-6 flex flex-col gap-3">
              <h2 className="font-rubik text-xs uppercase tracking-wide text-v5-neutral-400">
                Read till the End!
              </h2>
              <BookmarksScrubber
                currentPage={currentPage}
                numPages={numPages}
              />
            </div>
          )}

          {graduatesData.length > 0 && (
            <div className="mb-6 flex flex-col gap-3">
              <h2 className="font-manrope text-sm font-bold text-v5-neutral-600">
                Crio Grads have Cracked their Dream Careers In
              </h2>
              <div className="overflow-hidden">
                <BrochureAlumniCarousel
                  data={shuffledGraduates}
                />
              </div>
            </div>
          )}

          <AdvisorCtaFooter onCtaClick={analytics?.reportAdvisorCtaClick} />
        </div>
      </div>
    </>
  );
};

export default MobileBrochureSidebar;
